import type {
  CellClickedEvent,
  GridApi,
  GridReadyEvent,
  ModelUpdatedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ProjectId, TaskIdV2 } from 'api/models';
import type { GridChangeEvent } from 'components/organisms/grid-custom-column';
import { useTaskDrawer } from 'components/organisms/task-drawer';
import {
  LogType,
  RouteLevelEventTags,
  useLoadLoggerForDataDog,
} from 'hooks/use-data-dog-logger';
import { TaskDrawerTab } from 'hooks/use-navigate-to-task';
import { Loaded } from 'stores/core';
import type { Task } from 'stores/tasks';
import { isChildNode } from 'utils/is-child-node';
import {
  AllTasksEmptyState,
  GridContainer,
  LoadingCell,
  Wrapper,
} from '../all-tasks-v2/components';
import { BulkEditor } from '../bulk-editor';
import { useDeleteTasks } from '../use-delete-tasks';
import { useUpdateServerTasks } from '../use-my-task-update-tasks ';
import { StyledGrid } from './components';
import { DEFAULT_GRID_OPTIONS } from './grid-options';

interface Props {
  fieldsLoading: boolean;
  isLoading: boolean;
  handleGridChange: (data: GridChangeEvent) => void;
  onGridReady: (event: GridReadyEvent) => void;
  isPaginated: boolean;
  selected: Task[];
  setSelected: (selections: Task[]) => void;
  gridApi?: GridApi;
  isTreeDataUsed: boolean;
  projectId: number;
}

const TasksEmptyState = () => {
  const { executeLogger } = useLoadLoggerForDataDog(
    [RouteLevelEventTags.TasksListPageLoad],
    LogType.CallbackLog,
  );

  useEffect(() => {
    executeLogger();
  }, [executeLogger]);

  return <AllTasksEmptyState />;
};

export const TaskListGrid = ({
  fieldsLoading,
  isLoading,
  handleGridChange,
  onGridReady,
  isPaginated,
  selected,
  setSelected,
  gridApi,
  isTreeDataUsed,
  projectId,
}: Props) => {
  const { updateMultipleTasks } = useUpdateServerTasks();
  const [[taskDrawerState], show, hide] = useTaskDrawer();

  const onSelectionChanged = useCallback(
    ({ api }: SelectionChangedEvent) => {
      const selectedData = api.getSelectedNodes().reduce((acc, node) => {
        if (node.id != null) {
          acc.push(node.data);
        }

        return acc;
      }, [] as Task[]);

      if (selectedData.length !== 0 && taskDrawerState === 'show') {
        hide();
      }
      setSelected(selectedData);
    },
    [hide, setSelected, taskDrawerState],
  );

  const onCellClicked = useCallback(
    (event: CellClickedEvent) => {
      if (event.colDef.field !== 'status') {
        return false;
      }
      if (
        event.node.id === 'provisional' &&
        event.rowIndex != null &&
        event.column.getColId() === 'status'
      ) {
        event.api.startEditingCell({
          colKey: event.column,
          rowIndex: event.rowIndex,
        });

        return true;
      }
      const target = event.event?.target as HTMLElement;

      if (isChildNode(['task-field-value'])(target) && event.rowIndex != null) {
        event.api.startEditingCell({
          colKey: event.column,
          rowIndex: event.rowIndex,
        });
      } else {
        event.api.clearFocusedCell();
        const task = event.data as Task;

        show(
          ProjectId(task.project),
          TaskIdV2(task.taskId),
          TaskDrawerTab.Conversations,
        );
      }

      return true;
    },
    [show],
  );
  const onDelete = useDeleteTasks(isPaginated, gridApi);

  const isPrivate = useMemo(
    () => selected.some((task) => task!.private),
    [selected],
  );

  const getLookupForSelectedItem = useCallback(() => {
    const selectedLookup = selected.map((task) =>
      Loaded(task),
    ) as Loaded<Task>[];

    return selectedLookup.reduce((pre, [s, t]) => {
      pre[t.taskId] = [s, t];

      return pre;
    }, {});
  }, [selected]);

  const [rowCount, setRowCount] = useState(isLoading ? Number.MAX_VALUE : 0);
  const onModelUpdated = useCallback(({ api }: ModelUpdatedEvent) => {
    setRowCount(api.getDisplayedRowCount());
  }, []);

  const { executeLogger } = useLoadLoggerForDataDog(
    [RouteLevelEventTags.TasksListPageLoad],
    LogType.CallbackLog,
  );

  if (fieldsLoading) {
    return null;
  }

  return (
    <Wrapper>
      <GridContainer
        className={classNames({
          'ag-theme-balham': true,
          'multi-select': selected.length > 0,
        })}
      >
        <StyledGrid
          {...DEFAULT_GRID_OPTIONS}
          isPaginated={isPaginated}
          loading={isLoading}
          onCellClick={onCellClicked}
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChanged}
          onColumnVisible={handleGridChange}
          onColumnMoved={handleGridChange}
          onNewColumnsLoaded={handleGridChange}
          onSortChanged={handleGridChange}
          onModelUpdated={onModelUpdated}
          loadingCellRenderer={LoadingCell}
          treeData
          isServerSideGroup={(d) => isTreeDataUsed && !!d.subTaskOrder?.length}
          onFirstDataRendered={executeLogger}
        />
      </GridContainer>
      <BulkEditor
        ids={selected.map((x) => String(x.taskId))}
        getLookup={getLookupForSelectedItem}
        onUpdate={updateMultipleTasks}
        onDelete={onDelete}
        onClose={() => gridApi?.deselectAll()}
        projectIds={[ProjectId(projectId)]}
        isPrivate={isPrivate}
      />
      {!isLoading && rowCount === 0 ? <TasksEmptyState /> : null}
    </Wrapper>
  );
};
